import * as THREE from 'three';
import { G, M, TINT, HP, glowMat, mesh, bevel, plate, rib, seam, cable, mergeBones, stages, boneG } from './kit';
/* ============================================================
   REQUIEM 安魂曲 —— 最終・殘骸船
   ============================================================

   剪影：船身嵌滿 24 具玩家機甲殘骸
   階段：'P1 殘骸全暗','P2 上層八具亮起（綠）','P3 二十四具全亮 —— 綠色不再只屬於你'

   由 `tools/port-bosses.mjs` 從 `enemy_sample_ani.html` 機械移植，
   **不要手改這個檔**：要改造型請改原型再重跑移植器，否則 1:1 就斷了。
   驗證：`node tools/port-bosses.mjs --check`。
   ⚠️ 比對只守得住數字與字串，**動畫對不對只能實看**（圖鑑的開發模式）。 */


/* ── BOSS-26 REQUIEM 安魂曲 ── ⚠️ 全遊戲唯一一次，畫面上的綠色不是你。
   船身上嵌著 24 具被擊毀的玩家機甲，面罩還亮著。
   每過一階段亮起八具，最終階段全亮 —— 那時畫面上有 24 個綠點，
   而玩家整場遊戲學到的是「唯一的綠點是我」。

   這條規則被打破一次，而且只打破一次。代價很大，所以位置必須是最終 BOSS，
   而且必須用「這些真的是玩家機甲」來成立 —— 不是把敵人塗成綠的。 */
var RQN = 24;
export function buildRequiem(){
  var GLV = glowMat(TINT.vio.hex, 2.4), GLA = glowMat(TINT.amb.hex, 2.2);
  var root = new THREE.Group(), wrecks = [], i;
  var hull = boneG(root, 0, 8.60, 0);
  hull.add(bevel(M.dark, 7.20, 15.0, 6.00, [0, 0, 0]));
  hull.add(plate(M.armorF, 7.60, 6.20, 1.20, [0, 3.60, 3.10], 0.26));
  hull.add(plate(M.armorF, 7.20, 5.00, 1.10, [0,-3.40, 2.90], -0.30));
  hull.add(rib  (M.armorF, 0.30, 14.0, 0.30, [ 3.40, 0, 2.30]));
  hull.add(rib  (M.armorF, 0.30, 14.0, 0.30, [-3.40, 0, 2.30]));
  hull.add(mesh(G.cone, M.armorF, [4.20, 5.60, 4.20], [0, 9.60, 0]));
  hull.add(mesh(G.tor,  M.trim,  [8.40, 8.40, 0.50], [0, 6.20, 0], [HP,0,0]));
  hull.add(mesh(G.tor,  M.trim,  [9.20, 9.20, 0.50], [0,-2.40, 0], [HP,0,0]));
  hull.add(seam(6.00, 0.16, 0.36, [0, 6.20, 2.90]));
  hull.add(seam(6.40, 0.16, 0.36, [0, 1.40, 3.00]));
  hull.add(seam(6.00, 0.16, 0.36, [0,-2.40, 2.90]));
  for (i = 0; i < 3; i++)                                   // 三束外露線纜，只掛半邊
    hull.add(cable([2.60 - i*0.4, 5.80 - i*4.6, 2.10], 2.20));
  for (i = 0; i < 4; i++){
    var a = i*Math.PI * 2/4 + 0.78;
    hull.add(mesh(G.cyl, M.armorF, [1.20, 7.00, 1.20],
      [Math.sin(a)*4.20, -6.20, Math.cos(a)*4.20], [0.28*Math.cos(a),0,-0.28*Math.sin(a)]));
  }
  var vm = mesh(G.oct, GLV, [3.00,3.00,3.00], [0, 1.20, 0]);
  vm.userData.noMerge = 1; hull.add(vm);
  /* 三層、每層八具的機甲殘骸。用的是玩家材質 —— 這件事在造型上必須成立。 */
  for (i = 0; i < RQN; i++){
    var tier = Math.floor(i/8), j = i % 8;
    var b = j*Math.PI * 2/8 + tier*0.39;
    var r = 3.90 - tier*0.20, hy = 5.20 - tier*4.60;
    var w = boneG(hull, Math.sin(b)*r, hy, Math.cos(b)*r);
    w.rotation.y = b; w.rotation.z = (j % 3 - 1) * 0.34;                  // 每具歪的角度不同
    w.add(mesh(G.box, M.pDark, [0.80,1.10,0.60], [0, 0, 0.30]));
    w.add(mesh(G.box, M.pArm,  [1.00,0.44,0.50], [0, 0.60, 0.34], [0.2,0,0]));
    w.add(mesh(G.cyl, M.pDark, [0.28,1.00,0.28], [ 0.36,-0.80, 0.30], [0.4,0,0]));
    w.add(mesh(G.cyl, M.pDark, [0.28,0.90,0.28], [-0.36,-0.76, 0.24], [-0.3,0,0.2]));
    var vg = mesh(G.box, M.pGlow, [0.44,0.16,0.14], [0, 0.62, 0.62]);     // ← 面罩，綠的
    vg.userData.noMerge = 1; w.add(vg);
    wrecks.push({w:w, vg:vg, tier:tier, j:j});
  }
  root.userData.rig = {hull:hull, wrecks:wrecks, vm:vm, GLV:GLV, GLA:GLA};
  return mergeBones(root);
}
export function updRequiem(rig, o, dt){
  stages(rig, o, dt, 1.5);
  var s1 = rig.s1, s2 = rig.s2, s3 = rig.s3, i, W;
  for (i = 0; i < RQN; i++){
    W = rig.wrecks[i];
    var on = (W.tier === 0) ? s1 : s2;
    W.vg.scale.setScalar(0.15 + on*(1 + 0.35*Math.sin(o.t*2.6 + W.j*0.9)));
    W.w.rotation.x = 0.10*Math.sin(o.t*0.6 + i) * (0.3 + on);
    W.w.position.z = (W.tier - 1) * 0.0 + on*0.30;
  }
  /* 綠色只在該亮的時候亮 —— 材質是共用的，所以整體強度用最大的那一階控制 */
  M.pGlow.emissiveIntensity = 2.4 * (0.10 + 0.90*Math.max(s1, s2));
  rig.hull.rotation.y += dt*(0.10 + 0.65*s2);
  rig.hull.rotation.z = 0.06*Math.sin(o.t*0.5) + 0.10*s2;
  rig.hull.position.y = 8.60 + 0.34*Math.sin(o.t*0.6) + 1.10*s2;
  rig.vm.scale.setScalar((0.5 + 1.1*s2)*(1 + 0.12*Math.sin(o.t*2.8) + 0.8*o.tel));
  rig.GLV.emissiveIntensity = 2.4 + 2.4*o.tel + 6*o.atk*(1-o.atk) + 1.4*s2;
  rig.GLA.emissiveIntensity = 2.2 + 2.0*s2;
}